import React, {
  useEffect,
  useRef,
  useState,
} from "react";

import axios from "axios";
import { API_BASE } from "../../../config";

import { motion } from "framer-motion";

import {
  Send,
  Paperclip,
  ShieldCheck,
  User,
} from "lucide-react";

const TicketThread = ({
  selectedTicket,
  refreshTickets,
}) => {

  const [messages, setMessages] = useState([]);
  const [lastMessageCount, setLastMessageCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const [attachment, setAttachment] =
    useState(null);

  const messagesEndRef = useRef(null);

  const user =
    JSON.parse(localStorage.getItem("user"));

  const token = user?.token;

  // ====================================
  // AUTO SCROLL
  // ====================================
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  };

  // ====================================
  // FETCH MESSAGES
  // ====================================
  const fetchMessages = async () => {
    if (!selectedTicket?._id) return;

    try {
      setLoading(true);

      const res = await axios.get(
  `${API_BASE}/api/user/feedback/${selectedTicket._id}/messages`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const incoming =

Array.isArray(
res.data?.messages
)

?

res.data.messages

:

[];

if (

incoming.length !==

lastMessageCount

){

setMessages(
incoming
);

setLastMessageCount(
incoming.length
);

}

      refreshTickets();

    } catch (error) {
      console.error(
        "Fetch Messages Error:",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  // ====================================
  // INITIAL FETCH
  // ====================================
  useEffect(() => {

if (
selectedTicket?._id
){

fetchMessages();

}

}, [

selectedTicket?._id

]);

  // ====================================
  // AUTO SCROLL
  // ====================================
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // ====================================
  // SEND REPLY
  // ====================================
  const sendReply = async () => {
    if (!reply.trim() && !attachment)
      return;

    try {
      setSending(true);

      const formData = new FormData();

      formData.append("message", reply);

      if (attachment) {
        formData.append(
          "attachment",
          attachment
        );
      }

      await axios.post(
  `${API_BASE}/api/user/feedback/${selectedTicket._id}/reply`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type":
              "multipart/form-data",
          },
        }
      );

      setReply("");
      setAttachment(null);

      fetchMessages();

await refreshTickets();

    } catch (error) {
      console.error(
        "Reply Error:",
        error
      );
    } finally {
      setSending(false);
    }
  };

  // ====================================
  // NO TICKET SELECTED
  // ====================================
  if (!selectedTicket) {
    return (
      <div
        className="
          h-full
          flex
          items-center
          justify-center
          text-center
          p-6
        "
      >
        <div>
          <div
            className="
              w-24
              h-24
              mx-auto
              rounded-full
              bg-gradient-to-r
              from-indigo-500/20
              to-purple-500/20
              flex
              items-center
              justify-center
              mb-5
            "
          >
            <ShieldCheck
              size={42}
              className="text-indigo-400"
            />
          </div>

          <h2 className="text-2xl font-bold text-white">
            Support Center
          </h2>

          <p className="text-gray-400 mt-2">
            Select a conversation to start
            chatting with admins.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">

      {/* HEADER */}
      <div
        className="
          p-5
          border-b
          border-white/10
          flex
          items-center
          justify-between
          bg-white/[0.02]
        "
      >
        <div>
          <h2 className="text-xl font-bold text-white">
            {selectedTicket.subject}
          </h2>

          <div className="flex items-center gap-2 mt-1">

            <span
              className="
                px-3
                py-1
                rounded-full
                text-xs
                bg-indigo-500/20
                text-indigo-300
              "
            >
              {selectedTicket.category}
            </span>

            <span
              className="
                text-xs
                text-gray-400
              "
            >
              {selectedTicket.status}
            </span>
          </div>
        </div>
      </div>

      {/* MESSAGES */}
      <div
        className="
          flex-1
          overflow-y-auto
          px-5
          py-6
          space-y-5
          bg-gradient-to-b
          from-transparent
          to-black/10
        "
      >

        {/* LOADING */}
        {loading && (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="
                  h-20
                  rounded-2xl
                  bg-white/5
                  animate-pulse
                "
              />
            ))}
          </div>
        )}

        {/* MESSAGES */}
        {!loading &&
          messages.map((msg, index) => {

            const isUser =
              msg.senderType === "user";

            return (
              <motion.div
                key={msg._id}
                initial={{
                  opacity: 0,
                  y: 10,
                }}
                animate={{
                  opacity: 1,
                  y: 0,
                }}
                transition={{
                  delay: index * 0.03,
                }}
                className={`
                  flex
                  ${
                    isUser
                      ? "justify-end"
                      : "justify-start"
                  }
                `}
              >

                <div
                  className={`
                    max-w-[75%]
                    rounded-3xl
                    px-5
                    py-4
                    border
                    backdrop-blur-lg
                    shadow-xl

                    ${
                      isUser
                        ? `
                          bg-gradient-to-r
                          from-indigo-500
                          to-purple-500
                          border-indigo-400/30
                          text-white
                        `
                        : `
                          bg-white/[0.05]
                          border-white/10
                          text-gray-100
                        `
                    }
                  `}
                >

                  {/* SENDER */}
                  <div
                    className="
                      flex
                      items-center
                      gap-2
                      mb-2
                    "
                  >
                    {isUser ? (
                      <User size={15} />
                    ) : (
                      <ShieldCheck
                        size={15}
                      />
                    )}

                    <span
                      className="
                        text-xs
                        font-semibold
                        opacity-80
                      "
                    >
                      {isUser
                        ? "You"
                        : "Admin Team"}
                    </span>
                  </div>

                  {/* MESSAGE */}
                  <p
                    className="
                      whitespace-pre-wrap
                      break-words
                      text-sm
                      leading-relaxed
                    "
                  >
                    {msg.message}
                  </p>

                  {/* ATTACHMENT */}
                  {msg.attachment && (
                    <a
                      href={msg.attachment}
                      target="_blank"
                      rel="noreferrer"
                      className="
                        mt-3
                        inline-flex
                        items-center
                        gap-2
                        text-sm
                        underline
                        opacity-90
                        hover:opacity-100
                      "
                    >
                      <Paperclip size={14} />
                      View Attachment
                    </a>
                  )}

                  {/* TIME */}
                  <div
                    className="
                      mt-3
                      text-[11px]
                      opacity-60
                      text-right
                    "
                  >
                    {new Date(
                      msg.createdAt
                    ).toLocaleString()}
                  </div>
                </div>
              </motion.div>
            );
          })}

        <div ref={messagesEndRef} />
      </div>

      {/* REPLY BOX */}
      {!selectedTicket.isClosed && (
        <div
          className="
            p-4
            border-t
            border-white/10
            bg-black/10
          "
        >

          {/* ATTACHMENT PREVIEW */}
          {attachment && (
            <div
              className="
                mb-3
                text-sm
                text-indigo-300
                flex
                items-center
                gap-2
              "
            >
              <Paperclip size={14} />
              {attachment.name}
            </div>
          )}

          <div className="flex items-end gap-3">

            {/* INPUT */}
            <div className="flex-1">

              <textarea
                rows={2}
                value={reply}
                onChange={(e) =>
                  setReply(e.target.value)
                }
                placeholder="Type your message..."
                className="
                  w-full
                  rounded-2xl
                  bg-white/[0.05]
                  border
                  border-white/10
                  px-4
                  py-3
                  text-white
                  placeholder-gray-500
                  outline-none
                  resize-none
                  focus:border-indigo-500/50
                  transition
                "
              />
            </div>

            {/* FILE INPUT */}
            <label
              className="
                w-12
                h-12
                rounded-2xl
                bg-white/[0.05]
                border
                border-white/10
                flex
                items-center
                justify-center
                cursor-pointer
                hover:bg-white/[0.08]
                transition
              "
            >
              <Paperclip
                size={18}
                className="text-gray-300"
              />

              <input
                type="file"
                hidden
                onChange={(e) =>
                  setAttachment(
                    e.target.files[0]
                  )
                }
              />
            </label>

            {/* SEND BUTTON */}
            <button
              onClick={sendReply}
              disabled={sending}
              className="
                w-12
                h-12
                rounded-2xl
                bg-gradient-to-r
                from-indigo-500
                to-purple-500
                flex
                items-center
                justify-center
                text-white
                hover:scale-105
                transition
                disabled:opacity-50
              "
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketThread;